import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Gavel, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSelectedDate } from "@/context/DateContext";
import { RaceSelect } from "@/components/rikkus/RaceSelect";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import { TimestampET } from "@/components/rikkus/TimestampET";

const BET_TYPES = ["WIN", "TOP_K", "H2H", "MULTI_HORSE", "OTHER"];
const SIDES = ["FOR", "AGAINST"];
const PRICE_REFS = ["NYRA_TOTE", "BET365", "CIRCLE", "MANUAL"];

export default function SimulatePage() {
    const { date } = useSelectedDate();
    const [params, setParams] = useSearchParams();
    const fromOfferId = params.get("fromOffer");

    const [raceId, setRaceId] = useState(null);
    const [offerId, setOfferId] = useState(null);
    const [betType, setBetType] = useState("WIN");
    const [kThresh, setKThresh] = useState("");
    const [horses, setHorses] = useState("");
    const [side, setSide] = useState("FOR");
    const [predicted, setPredicted] = useState("");
    const [priceRef, setPriceRef] = useState("NYRA_TOTE");
    const [implied, setImplied] = useState("");
    const [stake, setStake] = useState("1");
    const [decision, setDecision] = useState("WOULD_BET");
    const [rationale, setRationale] = useState("");
    const [simbets, setSimbets] = useState([]);
    const [saving, setSaving] = useState(false);
    const [settling, setSettling] = useState(false);
    const [settleRaceId, setSettleRaceId] = useState(null);
    const [settleOpen, setSettleOpen] = useState(false);
    const [settleReport, setSettleReport] = useState(null);

    // Precarga desde oferta
    useEffect(() => {
        if (!fromOfferId) return;
        api.get(`/circle-offers`).then((r) => {
            const o = (r.data || []).find(x => String(x.offer_id) === String(fromOfferId));
            if (o) {
                setRaceId(o.race_id);
                setOfferId(o.offer_id);
                setBetType(o.bet_type);
                setKThresh(o.k_threshold ? String(o.k_threshold) : "");
                setHorses((o.horse_program_numbers || []).join(","));
                setSide(o.side || "FOR");
                setImplied(o.implied_probability != null ? String(o.implied_probability) : "");
                setPriceRef("CIRCLE");
                toast.info(`Formulario precargado desde oferta #${o.offer_id}`);
            }
        }).catch(() => {});
        // Limpiar el param para no repetir
        params.delete("fromOffer");
        setParams(params, { replace: true });
        // eslint-disable-next-line
    }, [fromOfferId]);

    const edge = useMemo(() => {
        const p = parseFloat(predicted); const i = parseFloat(implied);
        if (!isFinite(p) || !isFinite(i)) return null;
        return p - i;
    }, [predicted, implied]);

    const load = () => {
        api.get(`/simulated-bets?date=${date}`).then((r) => setSimbets(r.data || [])).catch(() => setSimbets([]));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

    const submit = async () => {
        if (!raceId) return toast.error("Selecciona carrera");
        const horseList = horses.split(",").map(s => s.trim()).filter(Boolean);
        if (horseList.length === 0) return toast.error("Ingresa caballos");
        if (betType === "TOP_K" && (!kThresh || parseInt(kThresh, 10) < 1)) return toast.error("TOP_K requiere k");
        const p = predicted ? parseFloat(predicted) : null;
        if (p != null && (p < 0 || p > 1)) return toast.error("predicted_probability debe estar en [0,1]");
        const im = implied ? parseFloat(implied) : null;
        if (im != null && (im < 0 || im > 1)) return toast.error("implied_probability debe estar en [0,1]");
        const body = {
            race_id: raceId,
            offer_id: offerId || null,
            bet_type: betType,
            k_threshold: betType === "TOP_K" ? parseInt(kThresh, 10) : null,
            horse_program_numbers: horseList,
            side,
            predicted_probability: p,
            price_reference: priceRef || null,
            implied_probability: im,
            stake_units: stake ? parseFloat(stake) : null,
            decision,
            decision_rationale: rationale || null,
        };
        setSaving(true);
        try {
            const { data } = await api.post("/simulated-bets", body);
            toast.success(`Simulación registrada #${data.sim_bet_id} (edge=${data.computed_edge ?? "—"})`);
            setRationale(""); setOfferId(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message || "Error");
        } finally { setSaving(false); }
    };

    const openSettle = () => {
        const races = Array.from(new Set(simbets.filter(s => !s.settled).map(s => s.race_id)));
        if (races.length === 0) { toast.info("No hay simulaciones sin liquidar"); return; }
        setSettleRaceId(races[0]);
        setSettleReport(null);
        setSettleOpen(true);
    };

    const doSettle = async () => {
        if (!settleRaceId) return;
        setSettling(true);
        try {
            const { data } = await api.post("/simulated-bets/settle", { race_id: settleRaceId });
            setSettleReport(data);
            toast.success(`Liquidadas ${data.settled}, saltadas ${data.skipped}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al liquidar");
        } finally { setSettling(false); }
    };

    const raceIdsUnsettled = Array.from(new Set(simbets.filter(s => !s.settled).map(s => s.race_id))).filter(Boolean);

    return (
        <>
            <Topbar title="Simulación" subtitle="Registro de apuestas simuladas (fase sombra, sin dinero real)" />
            <div className="p-3 md:p-4 grid grid-cols-1 lg:grid-cols-5 gap-4 flex-1 overflow-auto">
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="text-sm">Registrar simulación</CardTitle></CardHeader>
                        <CardContent>
                            <form data-testid="simulate-bet-form" onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label className="text-xs mb-1 block">Carrera</Label>
                                        <RaceSelect value={raceId} onChange={setRaceId} testid="simulate-race-select" />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1 block">Tipo</Label>
                                        <Select value={betType} onValueChange={setBetType}>
                                            <SelectTrigger data-testid="simulate-bet-type" className="h-9"><SelectValue /></SelectTrigger>
                                            <SelectContent>{BET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1 block">Side</Label>
                                        <Select value={side} onValueChange={setSide}>
                                            <SelectTrigger data-testid="simulate-side" className="h-9"><SelectValue /></SelectTrigger>
                                            <SelectContent>{SIDES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    {betType === "TOP_K" && (
                                        <div>
                                            <Label className="text-xs mb-1 block">k</Label>
                                            <Input data-testid="simulate-k-input" type="number" min={1} value={kThresh} onChange={(e) => setKThresh(e.target.value)} className="h-9 font-mono" />
                                        </div>
                                    )}
                                    <div className={betType === "TOP_K" ? "" : "col-span-2"}>
                                        <Label className="text-xs mb-1 block">Caballos (# programa)</Label>
                                        <Input data-testid="simulate-horses-input" value={horses} onChange={(e) => setHorses(e.target.value)} className="h-9 font-mono" placeholder="7 o 3,5" />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1 block">Prob. estimada</Label>
                                        <Input data-testid="simulate-probability-input" type="number" step="0.01" min={0} max={1} value={predicted} onChange={(e) => setPredicted(e.target.value)} className="h-9 font-mono" placeholder="0.40" />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1 block">Prob. implícita</Label>
                                        <Input data-testid="simulate-implied-input" type="number" step="0.01" min={0} max={1} value={implied} onChange={(e) => setImplied(e.target.value)} className="h-9 font-mono" placeholder="0.25" />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1 block">Fuente precio</Label>
                                        <Select value={priceRef} onValueChange={setPriceRef}>
                                            <SelectTrigger data-testid="simulate-price-ref" className="h-9"><SelectValue /></SelectTrigger>
                                            <SelectContent>{PRICE_REFS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1 block">Stake (unidades)</Label>
                                        <Input data-testid="simulate-stake-input" type="number" step="0.01" value={stake} onChange={(e) => setStake(e.target.value)} className="h-9 font-mono" />
                                    </div>
                                </div>
                                <div className="text-xs font-mono" data-testid="simulate-edge-value">
                                    edge = {edge != null ? (
                                        <span className={edge >= 0 ? "text-primary" : "text-fail"}>{edge.toFixed(4)}</span>
                                    ) : "—"}
                                </div>
                                <div>
                                    <Label className="text-xs mb-1 block">Decisión</Label>
                                    <ToggleGroup type="single" value={decision} onValueChange={(v) => v && setDecision(v)} className="justify-start">
                                        <ToggleGroupItem data-testid="simulate-would-bet" value="WOULD_BET" className="h-9 px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                                            WOULD_BET
                                        </ToggleGroupItem>
                                        <ToggleGroupItem data-testid="simulate-would-pass" value="WOULD_PASS" className="h-9 px-3 text-xs">
                                            WOULD_PASS
                                        </ToggleGroupItem>
                                    </ToggleGroup>
                                </div>
                                <div>
                                    <Label className="text-xs mb-1 block">Racional (opcional)</Label>
                                    <Textarea data-testid="simulate-rationale-input" value={rationale} onChange={(e) => setRationale(e.target.value)} rows={2} className="font-mono text-xs" />
                                </div>
                                <div className="pt-1">
                                    <Button data-testid="simulate-submit-button" type="submit" disabled={saving} className="w-full h-9">
                                        <Send className="h-3.5 w-3.5 mr-2" />
                                        {saving ? "Guardando…" : "Guardar simulación"}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
                <div className="lg:col-span-3">
                    <Card>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm">Simulaciones — {date}</CardTitle>
                            <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
                                <DialogTrigger asChild>
                                    <Button data-testid="simulate-settle-race-button" onClick={openSettle} size="sm" variant="secondary" className="h-8 text-xs">
                                        <Gavel className="h-3.5 w-3.5 mr-1.5" /> Liquidar carrera
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-md">
                                    <DialogHeader>
                                        <DialogTitle>Liquidar carrera</DialogTitle>
                                        <DialogDescription className="text-xs">
                                            Evalua todas las simulaciones no liquidadas contra los resultados oficiales.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-3">
                                        <Label className="text-xs">Carrera</Label>
                                        <Select value={settleRaceId ? String(settleRaceId) : ""} onValueChange={(v) => setSettleRaceId(parseInt(v, 10))}>
                                            <SelectTrigger data-testid="simulate-settle-race-select" className="h-9"><SelectValue placeholder="Elige…" /></SelectTrigger>
                                            <SelectContent>
                                                {raceIdsUnsettled.map((rid) => <SelectItem key={rid} value={String(rid)}>race_id {rid}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        {settleReport && (
                                            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs font-mono space-y-1" data-testid="simulate-settle-report">
                                                <div>settled: <span className="text-primary">{settleReport.settled}</span></div>
                                                <div>skipped: <span className="text-warn">{settleReport.skipped}</span></div>
                                                <div>hit_rate: {settleReport.hit_rate != null ? Number(settleReport.hit_rate).toFixed(4) : "—"}</div>
                                                <div>total_pnl_units: {settleReport.total_pnl_units != null ? Number(settleReport.total_pnl_units).toFixed(4) : "—"}</div>
                                                {settleReport.reasons && Object.keys(settleReport.reasons).length > 0 && (
                                                    <div>reasons: {JSON.stringify(settleReport.reasons)}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <DialogFooter>
                                        <Button data-testid="simulate-settle-confirm" onClick={doSettle} disabled={settling || !settleRaceId} className="h-9">
                                            {settling && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
                                            Ejecutar liquidación
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </CardHeader>
                        <CardContent>
                            <div className="rounded-md border border-border overflow-hidden">
                                <Table data-testid="simulate-table">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="h-8 text-[11px]">#</TableHead>
                                            <TableHead className="h-8 text-[11px]">Race</TableHead>
                                            <TableHead className="h-8 text-[11px]">Tipo</TableHead>
                                            <TableHead className="h-8 text-[11px]">Caballos</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Pred</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Impl</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Edge</TableHead>
                                            <TableHead className="h-8 text-[11px]">Dec.</TableHead>
                                            <TableHead className="h-8 text-[11px]">Estado</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">PnL</TableHead>
                                            <TableHead className="h-8 text-[11px]">Creado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {simbets.length === 0 && (
                                            <TableRow><TableCell colSpan={11} className="text-center text-xs text-muted-foreground py-8">Sin simulaciones para {date}.</TableCell></TableRow>
                                        )}
                                        {simbets.map((b) => (
                                            <TableRow key={b.sim_bet_id} data-testid={`simulate-row-${b.sim_bet_id}`} className="h-9">
                                                <TableCell className="font-mono text-xs">{b.sim_bet_id}</TableCell>
                                                <TableCell className="font-mono text-xs">R{b.race_number ?? "?"}</TableCell>
                                                <TableCell className="font-mono text-xs">{b.bet_type}{b.k_threshold ? `(${b.k_threshold})` : ""}</TableCell>
                                                <TableCell className="font-mono text-xs">{(b.horse_program_numbers || []).join(",")}</TableCell>
                                                <TableCell className="text-right font-mono text-xs">{b.predicted_probability != null ? Number(b.predicted_probability).toFixed(3) : "—"}</TableCell>
                                                <TableCell className="text-right font-mono text-xs">{b.implied_probability != null ? Number(b.implied_probability).toFixed(3) : "—"}</TableCell>
                                                <TableCell className={`text-right font-mono text-xs ${b.computed_edge != null && Number(b.computed_edge) >= 0 ? "text-primary" : "text-fail"}`}>{b.computed_edge != null ? Number(b.computed_edge).toFixed(3) : "—"}</TableCell>
                                                <TableCell>{b.decision === "WOULD_BET" ? <StatusBadge kind="would_bet">BET</StatusBadge> : <StatusBadge kind="would_pass">PASS</StatusBadge>}</TableCell>
                                                <TableCell>
                                                    {b.settled ? (b.hit ? <StatusBadge kind="hit">HIT</StatusBadge> : (b.decision === "WOULD_PASS" ? <StatusBadge kind="neutral">PASS</StatusBadge> : <StatusBadge kind="miss">MISS</StatusBadge>)) : <StatusBadge kind="pending">PENDING</StatusBadge>}
                                                </TableCell>
                                                <TableCell className={`text-right font-mono text-xs ${b.simulated_pnl_units != null && Number(b.simulated_pnl_units) > 0 ? "text-primary" : (Number(b.simulated_pnl_units) < 0 ? "text-fail" : "")}`}>
                                                    {b.simulated_pnl_units != null ? Number(b.simulated_pnl_units).toFixed(2) : "—"}
                                                </TableCell>
                                                <TableCell><TimestampET value={b.created_at} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </>
    );
}
